/** @graphql cc **/
import React, { useContext, useRef, useCallback } from 'react';
import { useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';

import type { PresenceActivity } from '@atlaskit/editor-common/collab';

import { Attribution, ErrorBoundary } from '@confluence/error-boundary';
import { fg } from '@confluence/feature-gating';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive';
import {
	NativeCollabPresenceStateContainer,
	useNativeCollabPresenceState,
} from '@confluence/native-collab';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage';
import {
	ExperienceTrackerContext,
	TEAM_PRESENCE_AVATAR_GROUP_VIEW_EXPERIENCE,
	ExperienceStart,
} from '@confluence/experience-tracker';
import { useIsGuest, useSessionData } from '@confluence/session-data';
import { useSpaceKey } from '@confluence/space-utils';
import { markErrorAsHandled } from '@confluence/graphql';
import { useLivePageMode } from '@confluence/live-pages-utils/entry-points/useLivePagesStore';

import {
	type PresenceParticipant,
	type PresenceSessionData,
	PresenceStoreContainer,
} from '../presence-store';
import type { CanvasCollaborators, ContentMode } from '../presenceTypes';
import { CURRENT_USER_DUMMY_SESSION_ID } from '../presenceUtils';
import { Presence } from '../page-presence/Presence';

import type {
	ContentHeaderPresenceUserQuery as ContentHeaderPresenceUserQuery$data,
	ContentHeaderPresenceUserQueryVariables as ContentHeaderPresenceUserQuery$variables,
	ContentHeaderPresenceUserQuery_user_AtlassianUser,
} from './__types__/ContentHeaderPresenceUserQuery';
import type {
	ContentHeaderPresenceContentSettingsQuery as ContentHeaderPresenceContentSettingsQuery$data,
	ContentHeaderPresenceContentSettingsQueryVariables as ContentHeaderPresenceContentSettingsQuery$variables,
} from './__types__/ContentHeaderPresenceContentSettingsQuery';
import { CanvasPresence } from './CanvasPresence';

export type ContentHeaderPresenceProps = {
	collaborators?: CanvasCollaborators | null;
	contentId: string;
	contentType?: string | null;
	isViewMode?: boolean;
	analyticSource?: string;
};

const createDefaultParticipant = ({
	presenceId,
	activity,
	user,
	isGuest,
}: {
	presenceId: string;
	activity: PresenceActivity;
	user?: ContentHeaderPresenceUserQuery_user_AtlassianUser | null;
	isGuest: boolean;
}): PresenceParticipant => {
	const sessions: PresenceSessionData = {
		[CURRENT_USER_DUMMY_SESSION_ID]: {
			activity,
		},
	};

	const usablePresenceId = user?.id ?? presenceId;

	return {
		id: usablePresenceId,
		userId: user?.id ?? 'unidentified',
		presenceId: usablePresenceId,
		name: user?.displayName ?? '',
		src: user?.photos?.[0]?.value ?? '',
		isCurrentUser: true,
		isGuest,
		sessions,
	};
};

export const ContentHeaderPresenceComponent = ({
	collaborators,
	contentId,
	contentType,
	isViewMode,
	analyticSource,
}: ContentHeaderPresenceProps) => {
	const experienceTracker = useContext(ExperienceTrackerContext);

	const { cloudId, isLoggedIn } = useSessionData();
	const isGuest = useIsGuest();
	const spaceKey = useSpaceKey();

	const isEditPage = useIsEditorPage();

	const isLivePage = useIsCurrentPageLive();
	const [{ isEditMode: isLiveEditMode }] = useLivePageMode();

	const { data: userData, error: userQueryError } = useQuery<
		ContentHeaderPresenceUserQuery$data,
		ContentHeaderPresenceUserQuery$variables
	>(gql`
		query ContentHeaderPresenceUserQuery {
			user: confluence_atlassianUser(current: true) {
				id
				displayName
				photos {
					value
					isPrimary
				}
			}
		}
	`);

	// presenceId is pulled from the native collab state to ensure that we have a consistent presenceId for a given contentId
	// across the collab providers that drive editor as well as presence components (telepointer and avatar colors).
	const [{ presenceId }] = useNativeCollabPresenceState();

	const presenceIdRef = useRef<string>(presenceId);

	let contentMode: ContentMode = 'view';

	if (isLivePage) {
		contentMode = 'live';
	} else if (isEditPage) {
		contentMode = 'edit';
	}

	const activity: PresenceActivity =
		contentMode === 'edit' || (contentMode === 'live' && (isLiveEditMode || !isViewMode))
			? 'editor'
			: 'viewer';

	const { data: settingData, error: settingsQueryError } = useQuery<
		ContentHeaderPresenceContentSettingsQuery$data,
		ContentHeaderPresenceContentSettingsQuery$variables
	>(
		gql`
			query ContentHeaderPresenceContentSettingsQuery($cloudId: ID!, $spaceKey: String!) {
				confluence_teamPresenceContentSetting(cloudId: $cloudId, spaceKey: $spaceKey) {
					isEnabledOnContentView
				}
			}
		`,
		{
			variables: {
				cloudId,
				spaceKey: spaceKey ?? '',
			},
			skip:
				!fg('confluence_team_presence_enable_on_view') ||
				contentType === 'whiteboard' ||
				contentMode !== 'view',
		},
	);

	const error = userQueryError || settingsQueryError;

	const isAnonymousBlockedOnView =
		!isLoggedIn && fg('confluence_team_presence_prevent_anonymous_on_view');

	const isSettingEnabledOnView = useCallback(() => {
		if (contentType === 'whiteboard' && fg('confluence_team_presence_whiteboard')) {
			return true;
		} else {
			return settingData?.confluence_teamPresenceContentSetting?.isEnabledOnContentView === true;
		}
	}, [contentType, settingData]);

	if (!contentType) {
		return null;
	}

	if (error) {
		experienceTracker.fail({
			name: TEAM_PRESENCE_AVATAR_GROUP_VIEW_EXPERIENCE,
			error,
		});

		markErrorAsHandled(error);

		return null;
	}

	if (contentMode === 'view') {
		if (!isSettingEnabledOnView()) {
			experienceTracker.abort({
				name: TEAM_PRESENCE_AVATAR_GROUP_VIEW_EXPERIENCE,
				reason: `Real-time visibility is disabled on view mode`,
			});

			return null;
		}

		if (isAnonymousBlockedOnView) {
			experienceTracker.abort({
				name: TEAM_PRESENCE_AVATAR_GROUP_VIEW_EXPERIENCE,
				reason: `Real-time visibility is disabled on view mode for anonymous users`,
			});

			return null;
		}
	}

	return (
		<PresenceStoreContainer
			participant={createDefaultParticipant({
				presenceId: presenceIdRef.current,
				activity,
				user: userData?.user,
				isGuest,
			})}
		>
			{contentType === 'whiteboard' && fg('confluence_team_presence_whiteboard') ? (
				<CanvasPresence
					collaborators={collaborators}
					contentId={contentId}
					contentType={contentType}
					analyticSource={analyticSource}
				/>
			) : (
				<Presence
					contentId={contentId}
					contentMode={contentMode}
					contentType={contentType}
					activity={activity}
					analyticSource={analyticSource}
				/>
			)}
		</PresenceStoreContainer>
	);
};

export const ContentHeaderPresence = (props: ContentHeaderPresenceProps) => {
	return (
		<ErrorBoundary attribution={Attribution.COLLABORATION_PRESENCE}>
			<NativeCollabPresenceStateContainer scope={props.contentId}>
				<ExperienceStart name={TEAM_PRESENCE_AVATAR_GROUP_VIEW_EXPERIENCE} />
				<ContentHeaderPresenceComponent {...props} />
			</NativeCollabPresenceStateContainer>
		</ErrorBoundary>
	);
};
